"use client";

import { useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Label } from "@/shadcn-components/ui/label";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/shadcn-components/ui/toggle-group";
import { cn } from "@/lib/utils";
import IndicatorColorPicker, {
  normalizeHex,
} from "@/components/chart/chart-indicators/indicators/common/IndicatorColorPicker";
import type { LineStyle } from "@/lib/chart-drawings/types";

const PRESET_COLORS = [
  "#FF9600",
  "#2196F3",
  "#26A69A",
  "#EF5350",
  "#FFA500",
  "#00C853",
  "#E040FB",
  "#9E9E9E",
];

function isPresetColor(color: string): boolean {
  const hex = normalizeHex(color);
  if (!hex) return false;
  return PRESET_COLORS.some((p) => p.toLowerCase() === hex.toLowerCase());
}

function LinePreview({
  color,
  lineStyle,
  width,
}: {
  color: string;
  lineStyle: LineStyle;
  width?: number;
}) {
  const px = Math.max(1, Math.min(4, width ?? 1));
  if (lineStyle === "dotted") {
    return (
      <div className="flex items-center gap-1 w-full max-w-[120px]">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="rounded-full shrink-0"
            style={{
              backgroundColor: color,
              height: `${px}px`,
              width: `${px}px`,
            }}
          />
        ))}
      </div>
    );
  }
  if (lineStyle === "dashed") {
    return (
      <div className="flex items-center gap-1 w-full max-w-[120px]">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="rounded-full shrink-0 w-5"
            style={{ backgroundColor: color, height: `${px}px` }}
          />
        ))}
      </div>
    );
  }
  return (
    <span
      className="block w-full max-w-[120px] rounded-full"
      style={{ backgroundColor: color, height: `${px}px` }}
    />
  );
}

interface DrawingStyleEditorProps {
  label: string;
  color: string;
  onColorChange: (color: string) => void;
  lineStyle?: LineStyle;
  onLineStyleChange?: (style: LineStyle) => void;
  lineWidth?: number;
  onLineWidthChange?: (width: number) => void;
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
}

export default function DrawingStyleEditor({
  label,
  color,
  onColorChange,
  lineStyle,
  onLineStyleChange,
  lineWidth,
  onLineWidthChange,
  fontSize,
  onFontSizeChange,
}: DrawingStyleEditorProps) {
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const colorBeforePickerRef = useRef(color);

  const customActive = customPickerOpen || !isPresetColor(color);
  const displayColor = normalizeHex(color) ?? color;

  const openCustomPicker = () => {
    colorBeforePickerRef.current = color;
    setCustomPickerOpen(true);
  };

  const closeCustomPicker = () => setCustomPickerOpen(false);

  const handleCustomCancel = () => {
    onColorChange(colorBeforePickerRef.current);
    closeCustomPicker();
  };

  const handleCustomApply = (hex: string) => {
    onColorChange(hex);
    colorBeforePickerRef.current = hex;
    closeCustomPicker();
  };

  return (
    <div className="py-3 border-b border-border/60 last:border-0 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        {lineStyle ? (
          <LinePreview color={color} lineStyle={lineStyle} width={lineWidth} />
        ) : (
          <span
            className="block h-4 w-12 rounded-md border border-border/80"
            style={{ backgroundColor: color }}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESET_COLORS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => {
              onColorChange(preset);
              closeCustomPicker();
            }}
            className={cn(
              "h-7 w-7 rounded-full border-2 transition-transform hover:scale-110",
              displayColor.toLowerCase() === preset.toLowerCase() &&
                !customPickerOpen
                ? "border-primary ring-2 ring-primary/30"
                : "border-border"
            )}
            style={{ backgroundColor: preset }}
            aria-label={`Color ${preset}`}
          />
        ))}
        <button
          type="button"
          onClick={() =>
            customPickerOpen ? handleCustomCancel() : openCustomPicker()
          }
          aria-expanded={customPickerOpen}
          className={cn(
            "h-7 min-w-[2.75rem] px-2 rounded-md border text-xs transition-colors flex items-center gap-1.5",
            customActive
              ? "border-primary ring-2 ring-primary/30 bg-muted/50"
              : "border-dashed border-border text-muted-foreground hover:bg-muted/50"
          )}
        >
          <span
            className="h-4 w-4 rounded-sm border border-border/80 shrink-0"
            style={{
              backgroundColor: customActive ? displayColor : undefined,
              backgroundImage: customActive
                ? undefined
                : "linear-gradient(135deg, #ef5350 0%, #ff9600 33%, #00c853 66%, #2196f3 100%)",
            }}
            aria-hidden
          />
          Custom
        </button>
      </div>

      {customPickerOpen ? (
        <IndicatorColorPicker
          label={label}
          color={color}
          onPreview={onColorChange}
          onApply={handleCustomApply}
          onCancel={handleCustomCancel}
        />
      ) : null}

      {lineStyle && onLineStyleChange ? (
        <ToggleGroup
          type="single"
          value={lineStyle}
          onValueChange={(v) => {
            if (v === "solid" || v === "dashed" || v === "dotted") {
              onLineStyleChange(v);
            }
          }}
          className="w-full justify-stretch"
        >
          <ToggleGroupItem value="solid" className="flex-1 text-xs h-8">
            Solid
          </ToggleGroupItem>
          <ToggleGroupItem value="dashed" className="flex-1 text-xs h-8">
            Dashed
          </ToggleGroupItem>
          <ToggleGroupItem value="dotted" className="flex-1 text-xs h-8">
            Dotted
          </ToggleGroupItem>
        </ToggleGroup>
      ) : null}

      {onLineWidthChange ? (
        <NumberStepper
          label="Width"
          value={lineWidth ?? 1}
          min={1}
          max={10}
          step={1}
          onChange={onLineWidthChange}
        />
      ) : null}

      {onFontSizeChange ? (
        <NumberStepper
          label="Font size"
          value={fontSize ?? 12}
          min={8}
          max={48}
          step={1}
          onChange={onFontSizeChange}
        />
      ) : null}
    </div>
  );
}

function NumberStepper({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-sm font-normal text-muted-foreground shrink-0">
        {label}
      </Label>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => onChange(clamp(value - step))}
          disabled={value <= min}
          aria-label={`Decrease ${label}`}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <Input
          type="number"
          value={value}
          onChange={(e) => {
            const n = parseFloat(e.target.value);
            if (!Number.isNaN(n)) onChange(clamp(n));
          }}
          min={min}
          max={max}
          step={step}
          className="h-7 w-[3.75rem] text-center text-sm tabular-nums px-1"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => onChange(clamp(value + step))}
          disabled={value >= max}
          aria-label={`Increase ${label}`}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
